/**
 * One surface's actions, with exactly one primary.
 *
 * It refuses a second `Primary` rather than rendering two, because two primary
 * actions is not a layout problem — it is an unresolved decision about what the
 * surface is for, and rendering both hands that decision to the reader.
 *
 * It is present in every composition at every width. The defect this closes is
 * a Mobile card that dropped its action entirely: a result a reader cannot act
 * on at 350px is a result they have to go somewhere else to use. Overflow
 * collapses into one disclosure instead of wrapping to a second row.
 */
"use client";
import type { ReactElement } from "react";
import {
  CresultcardActionBarLayout,
  CresultcardActionGroupLayout,
  CresultcardActionTone,
  type CresultcardActionSpec,
} from "./cresultcard.types";
import { CresultcardAction } from "./CresultcardAction";
import { CresultcardActionGroup } from "./CresultcardActionGroup";
import { CresultcardActionOverflow } from "./CresultcardActionOverflow";

const BAR_GAP = 7;
/** Past this the bar wraps, and a wrapped action bar is two action bars. */
const MAX_INLINE_SECONDARY = 2;

interface CresultcardActionBarProps {
  /**
   * Absent when the lane publishes no destination and no behaviour at all. The
   * action spec forces a real href or handler, so no inert control can ship.
   */
  readonly primary?: CresultcardActionSpec;
  /**
   * Other spellings of the SAME intent — Google Maps, Waze, Apple Maps behind
   * one Navigate. They ride the primary's own chevron, which is what separates
   * them from `secondary`: a secondary is a different thing to do, an alternate
   * is a different way to do this one.
   */
  readonly primaryAlternates?: readonly CresultcardActionSpec[];
  readonly secondary?: readonly CresultcardActionSpec[];
  readonly layout: CresultcardActionBarLayout;
}

export function CresultcardActionBar({
  primary,
  primaryAlternates = [],
  secondary = [],
  layout,
}: CresultcardActionBarProps): ReactElement | null {
  const fullWidth = layout === CresultcardActionBarLayout.FullWidth;
  // A second Primary is a contradiction, not an option: the extra ones are
  // demoted rather than dropped, so nothing a pillar declared disappears.
  const demoted = secondary.map((action): CresultcardActionSpec =>
    action.tone === CresultcardActionTone.Primary
      ? { ...action, tone: CresultcardActionTone.Secondary }
      : action,
  );
  const inline = demoted.slice(0, MAX_INLINE_SECONDARY);
  // A secondary that could not fit the bar is NOT an alternate spelling of the
  // primary. Merging the two put a vaccine-booking link inside a tray named
  // `More ways to navigate`; a tray is a claim that everything in it is one
  // intent, and overflow is the opposite of that. It gets its own control.
  const overflow = demoted.slice(MAX_INLINE_SECONDARY);
  const alternates = primaryAlternates;
  // A surface with no actions owns no action-bar space.
  if (primary == null && demoted.length === 0) {
    return null;
  }

  return (
    <div
      data-el="Cresultcard/ActionBar/Root"
      data-crc-layout={layout}
      style={{
        display: "flex",
        // A column bar centres nothing: the primary is the widest control on
        // the card, not the narrowest. `center` let the primary group shrink to
        // its content while every plain action set its own `width: 100%`, which
        // rendered the hierarchy upside down.
        alignItems: fullWidth ? "stretch" : "center",
        flexDirection: fullWidth ? "column" : "row",
        justifyContent:
          layout === CresultcardActionBarLayout.Trailing
            ? "flex-end"
            : "flex-start",
        gap: BAR_GAP,
        minWidth: 0,
      }}
    >
      {primary != null &&
        (alternates.length === 0 ? (
          <CresultcardAction
            fullWidth={fullWidth}
            href={primary.href}
            icon={primary.icon}
            label={primary.label}
            onPress={primary.onPress}
            tone={CresultcardActionTone.Primary}
            value={primary.value == null ? undefined : String(primary.value)}
          />
        ) : (
          <CresultcardActionGroup
            alternates={alternates}
            icon={primary.icon}
            label={primary.label}
            layout={
              fullWidth
                ? CresultcardActionGroupLayout.FullWidth
                : CresultcardActionGroupLayout.Inline
            }
            primary={primary}
            tone={CresultcardActionTone.Primary}
          />
        ))}
      {inline.map((action) => (
        <CresultcardAction
          compact={action.compact}
          fullWidth={fullWidth}
          href={action.href}
          icon={action.icon}
          key={action.label}
          label={action.label}
          onPress={action.onPress}
          tone={action.tone}
          value={action.value == null ? undefined : String(action.value)}
        />
      ))}
      {overflow.length > 0 && (
        <CresultcardActionOverflow
          actions={overflow}
          fullWidth={fullWidth}
        />
      )}
    </div>
  );
}
